import React, { useState, useCallback, memo } from "react";
import { FiUserPlus, FiUserCheck, FiLoader } from "react-icons/fi";
import { followApi } from "../../services/api/followApi";
import { toast } from "react-toastify";
import "./FollowButton.css";

/**
 * FollowButton - Optimistic UI Follow/Unfollow Button
 * 
 * Features:
 * - Optimistic UI updates (instant feedback)
 * - Static "Following" state (click to unfollow)
 * - Loading spinner during API call
 * - Batch updates for same author via callback
 * - Multiple variants: default, compact, icon-only
 * 
 * @param {string} authorId - The user ID to follow/unfollow
 * @param {boolean} isFollowing - Initial follow status from API
 * @param {boolean} isOwnPost - If true, button is not rendered
 * @param {string} variant - 'default' | 'compact' | 'icon-only'
 * @param {function} onFollowChange - Callback when follow status changes
 */
const FollowButton = ({
  authorId,
  isFollowing: initialFollowing,
  isOwnPost = false,
  variant = "default",
  onFollowChange,
}) => {
  const [isFollowing, setIsFollowing] = useState(initialFollowing);
  const [isLoading, setIsLoading] = useState(false);

  // Handle follow/unfollow with optimistic update
  // IMPORTANT: All hooks must be called before any early returns
  const handleClick = useCallback(async (e) => {
    e.stopPropagation(); // Prevent post card click
    e.preventDefault();
    
    if (isLoading) return; // Prevent double-clicks

    // Optimistic update
    const previousState = isFollowing;
    setIsFollowing(!isFollowing);
    setIsLoading(true);

    try {
      if (previousState) {
        await followApi.unfollowUser(authorId);
        toast.success("Unfollowed successfully");
      } else {
        await followApi.followUser(authorId);
        toast.success("Following!");
      }
      
      // Notify parent for global state update (batch update all posts by same author)
      onFollowChange?.(authorId, !previousState);
    } catch (error) {
      // Rollback on error
      setIsFollowing(previousState);
      toast.error(error.response?.data?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  }, [authorId, isFollowing, isLoading, onFollowChange]);

  // Don't render for own posts - AFTER all hooks are called
  if (isOwnPost) return null;

  // Icon-only variant (for mobile or compact layouts)
  if (variant === "icon-only") {
    return (
      <button
        type="button"
        className={`cbx-follow-btn-icon ${isFollowing ? "cbx-follow-btn-icon--following" : ""}`}
        onClick={handleClick}
        disabled={isLoading}
        aria-label={isFollowing ? "Unfollow" : "Follow"}
        title={isFollowing ? "Unfollow" : "Follow"}
      >
        {isLoading ? (
          <FiLoader className="cbx-follow-spinner" />
        ) : isFollowing ? (
          <FiUserCheck />
        ) : (
          <FiUserPlus />
        )}
      </button>
    );
  }

  // Compact variant (smaller button for tight spaces)
  if (variant === "compact") {
    return (
      <button
        type="button"
        className={`cbx-follow-btn cbx-follow-btn--compact ${
          isFollowing ? "cbx-follow-btn--following" : ""
        }`}
        onClick={handleClick}
        disabled={isLoading}
        aria-label={isFollowing ? "Unfollow" : "Follow"}
      >
        {isLoading ? (
          <FiLoader className="cbx-follow-spinner" />
        ) : isFollowing ? (
          <FiUserCheck size={14} />
        ) : (
          <FiUserPlus size={14} />
        )}
      </button>
    );
  }

  // Default variant (full button with text)
  return (
    <button
      type="button"
      className={`cbx-follow-btn ${
        isFollowing ? "cbx-follow-btn--following" : ""
      }`}
      onClick={handleClick}
      disabled={isLoading}
      aria-label={isFollowing ? "Unfollow" : "Follow"}
    >
      {isLoading ? (
        <FiLoader className="cbx-follow-spinner" />
      ) : isFollowing ? (
        <>
          <FiUserCheck size={14} />
          <span>Following</span>
        </>
      ) : (
        <>
          <FiUserPlus size={14} />
          <span>Follow</span>
        </>
      )}
    </button>
  );
};

// Memoize to prevent unnecessary re-renders
export default memo(FollowButton);
